import React, { useEffect } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'

import lang from '@/lang'
import { PasswordInput } from '@/modules/base/components/inputs'
import { SHOW_PASSWORD_ICON_VARIANTS } from '@/modules/base/components/inputs/password-input/password-input'
import { setPasswordData } from '@/redux/ducks/onboarding/main-v2'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { areAllPasswordRulesMet } from '@/modules/onboardingV2/helpers/password-rules'
import PasswordRuleChips from './password-rule-chips'

interface PasswordInputsProps {
  setInputsValid?: (isValid: boolean) => void
  // "Weiter" was clicked: an empty or too weak password is named below the field
  showError?: boolean
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'space-between',
  },
  // PasswordInput only takes a class for its container: the label is its first child, the error
  // ("Dies ist ein Pflichtfeld") the first span of the hints row at the end
  input: {
    '& > label, & > div:last-child > span:first-child': {
      fontSize: theme.brandTypography.text.regular.fontSize,
    },
    '& > label': {
      color: theme.palette.brand.text.heading,
    },
    // like the password field of the login: always white, 1 px border, radius 8, 48 px high
    '& input': {
      height: 48,
      borderRadius: theme.borderRadiusLarge,
      border: `1px solid ${theme.palette.brand.inputBorder}`,
      background: theme.palette.background.paper,
    },
    '& input:focus': {
      borderColor: theme.palette.brand.main,
      background: theme.palette.background.paper,
    },
    // the eye in the colour of the login's field icons
    '& svg': {
      color: theme.palette.brand.text.heading,
    },
  },
  inputError: {
    '& input, & input:focus': {
      borderColor: theme.palette.brand.error,
    },
  },
  error: {
    margin: '8px 0 0',
    color: theme.palette.brand.error,
    fontSize: theme.brandTypography.text.tiny.fontSize,
  },
}))

const PasswordInputs = ({ setInputsValid = () => {}, showError = false }: PasswordInputsProps) => {
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const { passwordData: { password } } = useAppSelector((state) => state.onboarding.mainV2)

  // valid once every chip is met - the same rules as "Passwort eingeben" and "Passwort vergeben"
  useEffect(() => {
    setInputsValid(areAllPasswordRulesMet(password))
  }, [password])

  const onPasswordChange = (value: string, name: string) => {
    dispatch(setPasswordData({ [name]: value }))
  }

  // the same messages as "Passwort eingeben"
  let error: string | null = null
  if (showError && !password) {
    error = lang.resetPasswordBrand.passwordRequired
  } else if (showError && !areAllPasswordRulesMet(password)) {
    error = lang.resetPasswordBrand.rulesNotMet
  }

  return (
    <div className={classes.root}>
      <PasswordInput
        withShowPassword
        showPasswordIconVariant={SHOW_PASSWORD_ICON_VARIANTS.brand}
        value={password}
        name='password'
        label={lang.registration.password.label}
        customClass={clsx(classes.input, error && classes.inputError)}
        onChange={onPasswordChange}
        required
        dataQsId='onboarding-password-input'
      />
      {error && <p className={classes.error} role='alert' data-qs-id='onboarding-password-error'>{error}</p>}
      <PasswordRuleChips password={password} highlightUnmet={Boolean(error) && Boolean(password)} dataQsId='onboarding-password-rules' />
    </div>
  )
}

export default PasswordInputs
